import { useMemo, useState } from "react";

import { t } from "../../../i18n";
import { cn } from "../../../lib/cn";
import { formatCurrency, formatDate, formatNumber } from "../../../lib/format";
import { trpc } from "../../../lib/trpc";
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardTitle,
  Dialog,
  Icon,
  IconButton,
  Money,
  Separator,
  Stack,
  useToast,
} from "../../ui";
import { NotifyMembersDialog } from "../dialogs/NotifyMembersDialog";
import { SettlementHistory } from "../dialogs/SettlementHistory";
import { useSettlementBalances } from "../hooks/useSettlementBalances";
import type { Member, Settlement } from "../types";

interface SummarySectionProps {
  settlement: Settlement;
  members: Member[];
  isLocked: boolean;
  ownerCoversInvoice: boolean;
  householdRole: "owner" | "member" | null;
}

export function SummarySection({
  settlement,
  members,
  isLocked,
  ownerCoversInvoice,
  householdRole,
}: SummarySectionProps) {
  const utils = trpc.useUtils();
  const { show } = useToast();
  const [confirmLockOpen, setConfirmLockOpen] = useState(false);
  const [confirmReopenOpen, setConfirmReopenOpen] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [copiedTransferKey, setCopiedTransferKey] = useState<string | null>(null);
  const approve = trpc.settlement.approve.useMutation({
    onSuccess: () => {
      utils.settlement.get.invalidate({ settlementId: settlement.id });
      utils.settlement.events.invalidate({ settlementId: settlement.id });
    },
  });
  const unapprove = trpc.settlement.unapprove.useMutation({
    onSuccess: () => {
      utils.settlement.get.invalidate({ settlementId: settlement.id });
      utils.settlement.events.invalidate({ settlementId: settlement.id });
    },
  });
  const lock = trpc.settlement.lock.useMutation({
    onSuccess: () => {
      utils.settlement.get.invalidate({ settlementId: settlement.id });
      utils.settlement.events.invalidate({ settlementId: settlement.id });
      setConfirmLockOpen(false);
    },
  });
  const reopen = trpc.settlement.reopen.useMutation({
    onSuccess: () => {
      utils.settlement.get.invalidate({ settlementId: settlement.id });
      utils.settlement.events.invalidate({ settlementId: settlement.id });
      setConfirmReopenOpen(false);
      show({
        tone: "success",
        title: t.settlement.summary.reopenSuccessToast,
        dismissLabel: t.common.close,
      });
    },
    onError: (err) => {
      show({
        tone: "danger",
        title: t.settlement.summary.reopenFailureToast(err.message),
        dismissLabel: t.common.close,
      });
    },
  });

  // Who-owes-whom + greedy creditor↔debtor pairing — extracted to
  // `useSettlementBalances` so ChartsSection and any future per-member
  // breakdown can share the same numbers without re-implementing the
  // math. The hook also keeps "owner covers invoice" semantics in one
  // place — see apps/api/src/db/queries/households.ts for the
  // server-side mirror.
  const { balances, transfers } = useSettlementBalances(settlement, members, ownerCoversInvoice);

  const memberMap = useMemo(() => new Map(members.map((m) => [m.userId, m])), [members]);
  const me = trpc.auth.me.useQuery();
  const myUserId = me.data?.id;
  // settlement_approvals is populated lazily — a row only exists after a
  // member has clicked Approve. We therefore can't use its length as the
  // denominator (it starts at 0 and grows). Treat each household member
  // as a row in the approvals list, and look up their approval state in
  // the lazy table when rendering.
  const approvalByUserId = new Map(settlement.memberApprovals.map((a) => [a.userId, a]));
  const approvedCount = settlement.memberApprovals.filter((a) => a.approvedAt).length;
  const totalApprovals = members.length;
  const allApproved = totalApprovals > 0 && approvedCount === totalApprovals;
  const isOwner = !!myUserId && settlement.createdBy === myUserId;
  // Reopen is a household-level permission, distinct from the per-settlement
  // creator check `isOwner` above. We keep the existing lock UI on
  // `isOwner` to avoid scope creep into how locking works, and gate the
  // new reopen action on the household role the backend also enforces.
  const isHouseholdOwner = householdRole === "owner";
  const approvalProgress = totalApprovals === 0 ? 0 : (approvedCount / totalApprovals) * 100;
  const myBalance = myUserId ? (balances.get(myUserId) ?? 0) : 0;
  const myBalanceEven = Math.abs(myBalance) < 0.01;
  const myBalanceIsCreditor = myBalance > 0;

  const handleCopyTransferAmount = async (key: string, amount: number) => {
    try {
      const text = formatNumber(amount).replace(/\s/g, "");
      await navigator.clipboard.writeText(text);
      setCopiedTransferKey(key);
      setTimeout(() => {
        setCopiedTransferKey((current) => (current === key ? null : current));
      }, 2000);
    } catch {
      // Clipboard unavailable — the amount is still visible for manual copy.
    }
  };

  return (
    <Card padding="md">
      <CardTitle className="mb-4 flex items-center gap-2">
        <Icon name="circle-dollar" size="sm" className="text-ink-500" />
        {t.settlement.summary.heading}
      </CardTitle>

      {/* Personal position — the first thing most users want to know. */}
      {myUserId && (
        <div
          className={cn(
            "mb-4 flex items-center gap-3 rounded-lg p-3",
            myBalanceEven
              ? "border border-ink-200 bg-ink-50"
              : myBalanceIsCreditor
                ? "border border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-900/30"
                : "border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/30",
          )}
        >
          <div
            className={cn(
              "flex h-10 w-10 shrink-0 items-center justify-center rounded-full",
              myBalanceEven
                ? "bg-ink-200 text-ink-700"
                : myBalanceIsCreditor
                  ? "bg-emerald-200 text-emerald-800 dark:bg-emerald-800 dark:text-emerald-100"
                  : "bg-amber-200 text-amber-800 dark:bg-amber-800 dark:text-amber-100",
            )}
          >
            <Icon name={myBalanceEven ? "check-circle" : "wallet"} size="md" />
          </div>
          <div className="min-w-0">
            <div
              className={cn(
                "font-medium text-xs uppercase tracking-wide",
                myBalanceEven
                  ? "text-ink-500"
                  : myBalanceIsCreditor
                    ? "text-emerald-700 dark:text-emerald-300"
                    : "text-amber-700 dark:text-amber-300",
              )}
            >
              {t.settlement.summary.yourPositionLabel}
            </div>
            <div
              className={cn(
                "tabular font-semibold text-base",
                myBalanceEven
                  ? "text-ink-800"
                  : myBalanceIsCreditor
                    ? "text-emerald-900 dark:text-emerald-100"
                    : "text-amber-900 dark:text-amber-100",
              )}
            >
              {myBalanceEven
                ? t.settlement.summary.yourPositionEven
                : myBalanceIsCreditor
                  ? t.settlement.summary.yourPositionReceives(
                      formatCurrency(myBalance, settlement.currency),
                    )
                  : t.settlement.summary.yourPositionOwes(
                      formatCurrency(-myBalance, settlement.currency),
                    )}
            </div>
          </div>
        </div>
      )}

      {/* Transfers */}
      <div className="mb-3 flex min-w-0 items-baseline gap-2">
        <h4 className="font-medium text-ink-700 text-sm">
          {t.settlement.summary.transfersHeading}
        </h4>
        {transfers.length > 0 && (
          <span className="text-ink-500 text-xs">
            {t.settlement.summary.transferCountHint(transfers.length)}
          </span>
        )}
      </div>
      {transfers.length === 0 ? (
        <p className="mb-6 text-ink-500 text-sm">{t.settlement.summary.noTransfers}</p>
      ) : (
        <Stack gap={2} className="mb-6">
          {transfers.map((transfer) => {
            const from = memberMap.get(transfer.from);
            const to = memberMap.get(transfer.to);
            const involvesMe =
              !!myUserId && (transfer.from === myUserId || transfer.to === myUserId);
            const transferKey = `${transfer.from}->${transfer.to}:${transfer.amount}`;
            const amountCopied = copiedTransferKey === transferKey;
            return (
              <div
                key={transferKey}
                className={cn(
                  "flex flex-col gap-1.5 rounded-md border px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-2",
                  involvesMe
                    ? "border-accent-200 bg-accent-50 dark:border-accent-800 dark:bg-accent-900/30"
                    : "border-transparent bg-ink-50",
                )}
              >
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <Avatar name={from?.displayName ?? from?.email ?? "?"} size="sm" />
                  <span className="min-w-0 truncate text-ink-800 text-sm">
                    {from?.displayName ?? from?.email}
                    {transfer.from === myUserId && (
                      <span className="text-ink-400">{t.settlement.summary.youSuffix}</span>
                    )}
                  </span>
                  <span className="shrink-0 text-ink-500 text-sm">
                    {t.settlement.summary.transferVerb}
                  </span>
                  <Icon name="arrow-right" size="sm" className="shrink-0 text-ink-400" />
                  <Avatar name={to?.displayName ?? to?.email ?? "?"} size="sm" />
                  <span className="min-w-0 truncate text-ink-800 text-sm">
                    {to?.displayName ?? to?.email}
                    {transfer.to === myUserId && (
                      <span className="text-ink-400">{t.settlement.summary.youSuffix}</span>
                    )}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-1 self-end sm:ml-2 sm:self-auto">
                  <Money
                    amount={transfer.amount}
                    currency={settlement.currency}
                    className={cn(
                      "font-semibold text-sm",
                      involvesMe ? "text-accent-900 dark:text-accent-100" : "text-ink-900",
                    )}
                  />
                  <IconButton
                    icon={amountCopied ? "check" : "copy"}
                    label={
                      amountCopied
                        ? t.settlement.summary.copyTransferAmountCopiedAriaLabel
                        : t.settlement.summary.copyTransferAmountAriaLabel(
                            formatCurrency(transfer.amount, settlement.currency),
                          )
                    }
                    size="sm"
                    onClick={() => handleCopyTransferAmount(transferKey, transfer.amount)}
                    className={cn(
                      amountCopied &&
                        "text-emerald-700 hover:text-emerald-700 dark:text-emerald-300 dark:hover:text-emerald-300",
                    )}
                  />
                </div>
              </div>
            );
          })}
        </Stack>
      )}

      <Separator className="my-4" />

      {/* Engagement card — ren og luftig presentasjon av godkjenningsstatus.
          Tre tilstander: låst (ferdig), klar-til-å-gjøre-opp (alle godkjent),
          og pågående. Hver har sin egen farge, ikon og hjelpetekst. */}
      {totalApprovals > 0 && (
        <div
          className={cn(
            "mb-4 rounded-xl border p-5 transition-colors",
            isLocked
              ? "border-emerald-300 bg-emerald-100/70 dark:border-emerald-800 dark:bg-emerald-950/50"
              : allApproved
                ? "border-emerald-200 bg-emerald-50/70 dark:border-emerald-900 dark:bg-emerald-950/40"
                : "border-accent-200 bg-accent-50/60 dark:border-accent-900 dark:bg-accent-950/40",
          )}
        >
          {/* Topp-rad: ikon + tittel/hint til venstre, "Send paminnelse" til
              hoyre. Pa mobil stacker knappen under (flex-col → flex-row). */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <div className="flex min-w-0 items-center gap-3.5">
              <div
                className={cn(
                  "flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-white shadow-subtle",
                  isLocked ? "bg-emerald-600" : allApproved ? "bg-emerald-500" : "bg-accent-500",
                )}
              >
                <Icon name={isLocked ? "lock" : allApproved ? "sparkles" : "users"} size="md" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-base text-ink-900 leading-tight">
                  {isLocked
                    ? t.settlement.summary.progressTitleLocked
                    : t.settlement.summary.progressTitle(approvedCount, totalApprovals)}
                </div>
                <p className="mt-1 text-ink-600 text-xs leading-snug">
                  {isLocked
                    ? settlement.lockedAt
                      ? t.settlement.summary.progressHintLocked(formatDate(settlement.lockedAt))
                      : t.settlement.summary.progressHintAll
                    : allApproved
                      ? t.settlement.summary.progressHintAll
                      : approvedCount === 0
                        ? t.settlement.summary.progressHintNone
                        : t.settlement.summary.progressHintPartial(totalApprovals - approvedCount)}
                </p>
              </div>
            </div>
            {isOwner && !isLocked && !allApproved && (
              <Button
                size="sm"
                iconLeft="mail"
                onClick={() => setNotifyOpen(true)}
                className="shrink-0 self-stretch border border-accent-300 bg-accent-100 text-accent-800 hover:border-accent-400 hover:bg-accent-200 sm:self-auto dark:border-accent-800 dark:bg-accent-900/40 dark:text-accent-100 dark:hover:bg-accent-900/60"
              >
                {t.settlement.summary.notifyButton}
              </Button>
            )}
          </div>
          <div
            className={cn(
              "mt-4 h-1.5 overflow-hidden rounded-full",
              isLocked
                ? "bg-emerald-200/70 dark:bg-emerald-900/50"
                : allApproved
                  ? "bg-emerald-200/60 dark:bg-emerald-900/40"
                  : "bg-accent-200/60 dark:bg-accent-900/40",
            )}
            aria-hidden="true"
          >
            <div
              className={cn(
                "h-full rounded-full transition-all duration-500",
                isLocked ? "bg-emerald-600" : allApproved ? "bg-emerald-500" : "bg-accent-500",
              )}
              style={{ width: `${approvalProgress}%` }}
            />
          </div>
        </div>
      )}

      <h4 className="mb-3 font-medium text-ink-700 text-sm">
        {t.settlement.summary.approvalsHeading(approvedCount, totalApprovals)}
      </h4>
      <Stack gap={2}>
        {members.map((m) => {
          const approval = approvalByUserId.get(m.userId);
          const approvedAt = approval?.approvedAt ?? null;
          const isMe = m.userId === myUserId;
          return (
            <div
              key={m.userId}
              className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex min-w-0 items-center gap-2">
                <Avatar name={m.displayName ?? m.email ?? "?"} size="sm" />
                <span className="min-w-0 truncate text-ink-800 text-sm">
                  {m.displayName ?? m.email}
                  {isMe && <span className="text-ink-400">{t.settlement.summary.youSuffix}</span>}
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-2 self-end sm:self-auto">
                {approvedAt ? (
                  <>
                    <Badge tone="success" iconLeft="check-circle">
                      {t.settlement.summary.approvedAt(formatDate(approvedAt))}
                    </Badge>
                    {isMe && !isLocked && (
                      <Button
                        variant="ghost"
                        size="sm"
                        isLoading={unapprove.isPending}
                        onClick={() => unapprove.mutate({ settlementId: settlement.id })}
                      >
                        {t.settlement.summary.undoApproveButton}
                      </Button>
                    )}
                  </>
                ) : isMe && !isLocked ? (
                  <Button
                    variant="primary"
                    size="sm"
                    iconLeft="check"
                    isLoading={approve.isPending}
                    onClick={() => approve.mutate({ settlementId: settlement.id })}
                  >
                    {t.settlement.summary.approveButton}
                  </Button>
                ) : (
                  <Badge tone="warning" iconLeft="lightbulb" size="md">
                    {t.settlement.summary.pending}
                  </Badge>
                )}
              </div>
            </div>
          );
        })}
      </Stack>

      <Separator className="my-4" />
      <SettlementHistory
        settlementId={settlement.id}
        createdAt={settlement.createdAt}
        createdBy={settlement.createdBy}
        memberApprovals={settlement.memberApprovals}
        lockedAt={settlement.lockedAt}
        members={members}
        myUserId={myUserId ?? null}
      />

      {/* Lock CTA — only the owner can settle */}
      {!isLocked && isOwner && (
        <>
          <Separator className="my-4" />
          {allApproved ? (
            <div className="flex flex-col gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-3 sm:flex-row sm:items-center sm:justify-between dark:border-emerald-800 dark:bg-emerald-900/30">
              <div className="flex min-w-0 items-start gap-2">
                <Icon
                  name="check-circle"
                  size="sm"
                  className="mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-300"
                />
                <span className="text-emerald-900 text-sm dark:text-emerald-100">
                  {t.settlement.summary.allApprovedHint}
                </span>
              </div>
              <Button
                variant="primary"
                size="sm"
                iconLeft="lock"
                onClick={() => setConfirmLockOpen(true)}
                className="w-full shrink-0 sm:w-auto"
              >
                {t.settlement.summary.lockButton}
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-3 rounded-md border border-amber-200 bg-amber-50 p-3 sm:flex-row sm:items-center sm:justify-between dark:border-amber-800 dark:bg-amber-900/30">
              <div className="flex min-w-0 items-start gap-2">
                <Icon
                  name="alert-circle"
                  size="sm"
                  className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-300"
                />
                <span className="text-amber-900 text-sm dark:text-amber-100">
                  {t.settlement.summary.lockBeforeAllApprovedHint(approvedCount, totalApprovals)}
                </span>
              </div>
              <Button
                variant="primary"
                size="sm"
                iconLeft="lock"
                onClick={() => setConfirmLockOpen(true)}
                className="w-full shrink-0 sm:w-auto"
              >
                {t.settlement.summary.lockButton}
              </Button>
            </div>
          )}
        </>
      )}

      {!isLocked && !isOwner && allApproved && (
        <>
          <Separator className="my-4" />
          <div className="flex items-center gap-2 rounded-md border border-ink-200 bg-ink-50 p-3">
            <Icon name="check-circle" size="sm" className="text-ink-500" />
            <span className="text-ink-700 text-sm">{t.settlement.summary.waitingForOwnerHint}</span>
          </div>
        </>
      )}

      {isLocked && settlement.lockedAt && (
        <>
          <Separator className="my-4" />
          {allApproved ? (
            <div className="flex flex-col gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-3 sm:flex-row sm:items-center sm:justify-between dark:border-emerald-800 dark:bg-emerald-900/30">
              <div className="flex min-w-0 items-start gap-2">
                <Icon
                  name="lock"
                  size="sm"
                  className="mt-0.5 shrink-0 text-emerald-700 dark:text-emerald-300"
                />
                <span className="text-emerald-900 text-sm dark:text-emerald-100">
                  {t.settlement.summary.lockedAt(formatDate(settlement.lockedAt))}
                </span>
              </div>
              {isHouseholdOwner && (
                <Button
                  size="sm"
                  iconLeft="unlock"
                  onClick={() => setConfirmReopenOpen(true)}
                  className="w-full shrink-0 sm:w-auto"
                >
                  {t.settlement.summary.reopenButton}
                </Button>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-3 rounded-md border border-amber-200 bg-amber-50 p-3 sm:flex-row sm:items-center sm:justify-between dark:border-amber-800 dark:bg-amber-900/30">
              <div className="flex min-w-0 items-start gap-2">
                <Icon
                  name="alert-circle"
                  size="sm"
                  className="mt-0.5 shrink-0 text-amber-700 dark:text-amber-300"
                />
                <span className="text-amber-900 text-sm dark:text-amber-100">
                  {t.settlement.summary.lockedAtWithoutAllApproved(
                    formatDate(settlement.lockedAt),
                    approvedCount,
                    totalApprovals,
                  )}
                </span>
              </div>
              {isHouseholdOwner && (
                <Button
                  size="sm"
                  iconLeft="unlock"
                  onClick={() => setConfirmReopenOpen(true)}
                  className="w-full shrink-0 sm:w-auto"
                >
                  {t.settlement.summary.reopenButton}
                </Button>
              )}
            </div>
          )}
        </>
      )}

      <Dialog
        open={confirmLockOpen}
        onClose={() => {
          if (!lock.isPending) setConfirmLockOpen(false);
        }}
        title={t.settlement.summary.lockConfirmDialog.title}
        description={
          allApproved
            ? t.settlement.summary.lockConfirmDialog.descriptionAllApproved
            : t.settlement.summary.lockConfirmDialog.descriptionNotAllApproved(
                approvedCount,
                totalApprovals,
              )
        }
        footer={
          <>
            <Button onClick={() => setConfirmLockOpen(false)} disabled={lock.isPending}>
              {t.common.cancel}
            </Button>
            <Button
              variant="primary"
              iconLeft="lock"
              isLoading={lock.isPending}
              onClick={() => lock.mutate({ settlementId: settlement.id })}
            >
              {t.settlement.summary.lockConfirmDialog.submit}
            </Button>
          </>
        }
      >
        <p className="text-ink-600 text-sm">{t.settlement.summary.lockConfirmDialog.warning}</p>
      </Dialog>

      <Dialog
        open={confirmReopenOpen}
        onClose={() => {
          if (!reopen.isPending) setConfirmReopenOpen(false);
        }}
        title={t.settlement.summary.reopenConfirmDialog.title}
        description={t.settlement.summary.reopenConfirmDialog.description}
        footer={
          <>
            <Button onClick={() => setConfirmReopenOpen(false)} disabled={reopen.isPending}>
              {t.common.cancel}
            </Button>
            <Button
              variant="primary"
              iconLeft="unlock"
              isLoading={reopen.isPending}
              onClick={() => reopen.mutate({ settlementId: settlement.id })}
            >
              {t.settlement.summary.reopenConfirmDialog.submit}
            </Button>
          </>
        }
      >
        <p className="text-ink-600 text-sm">{t.settlement.summary.reopenConfirmDialog.warning}</p>
      </Dialog>

      <NotifyMembersDialog
        open={notifyOpen}
        onClose={() => setNotifyOpen(false)}
        settlementId={settlement.id}
        pendingMembers={members.filter(
          (m) => m.userId !== myUserId && !approvalByUserId.get(m.userId)?.approvedAt,
        )}
      />
    </Card>
  );
}
